import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import {
  ArrowLeft, ShoppingBag, MessageCircle, Star, Loader2,
  Sparkles, Check, Package, Heart,
} from "lucide-react";
import { useCart } from "../context/CartContext";
import { useChat } from "../context/ChatContext";
import { useAuth } from "../context/AuthContext";
import { useTheme } from "../context/ThemeContext";
import { useWishlist } from "../context/WishlistContext";
import { isPlaceholderPrice, priceLabel } from "../utils/price";
import { stockForVariant, priceForSize } from "../utils/variant";
import { bgWithImageStyle } from "../utils/themeBg";
import { pricePer100, recommendedSize, compareAtForSize, formatGroups, recommendedLabel } from "../utils/formatMetrics";
import { getBundleSuggestion, applyBundleUpgrade } from "../utils/bundleSuggestion";
import Header from "../components/leovera/Header";
import Footer from "../components/leovera/Footer";
import BundleCard from "../components/leovera/BundleCard";
import NotFound from "./NotFound";
import { trackViewItem } from "../components/leovera/Analytics";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Stars = ({ value, onChange, size = "w-4 h-4", testid }) => (
  <div className="inline-flex items-center gap-0.5" data-testid={testid}>
    {[1, 2, 3, 4, 5].map((n) => (
      <button key={n} type="button" disabled={!onChange}
              onClick={() => onChange && onChange(n)}
              className={onChange ? "cursor-pointer" : "cursor-default"}>
        <Star className={`${size} ${n <= Math.round(value) ? "text-amber-300 fill-amber-300" : "text-white/20"}`} />
      </button>
    ))}
  </div>
);

const setMeta = (name, content, prop = false) => {
  const attr = prop ? "property" : "name";
  let el = document.head.querySelector(`meta[${attr}="${name}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, name);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content || "");
};

export default function ProductPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const cart = useCart();
  const chat = useChat();
  const { user, userApi } = useAuth();
  const { settings } = useTheme();
  const { isWishlisted, toggle: toggleWishlist } = useWishlist();

  const [data, setData] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [reviews, setReviews] = useState({ avg: 0, count: 0, reviews: [] });
  const [myRating, setMyRating] = useState(5);
  const [myText, setMyText] = useState("");
  const [sending, setSending] = useState(false);
  const [sentMsg, setSentMsg] = useState("");
  const [pickedFlavor, setPickedFlavor] = useState("");
  const [pickedSize, setPickedSize] = useState("");
  const [activeImg, setActiveImg] = useState(0);
  const [catalog, setCatalog] = useState([]);
  const [bundles, setBundles] = useState([]);

  const product = data?.product;

  useEffect(() => {
    axios.get(`${API}/catalog`).then(({ data: d }) => setCatalog(d || [])).catch(() => {});
    axios.get(`${API}/bundles`).then(({ data: d }) => setBundles(d || [])).catch(() => {});
  }, []);

  const productsById = useMemo(() => {
    const map = {};
    catalog.forEach((p) => { map[p.id] = p; });
    return map;
  }, [catalog]);

  const matchingBundles = useMemo(() => {
    if (!product) return [];
    return bundles.filter((b) => (b.items || []).some((it) => it.product_id === product.id));
  }, [bundles, product]);

  // Se il cliente ha già 2+ altri prodotti di uno di questi kit nel carrello,
  // suggeriamo di attivarlo: lo stesso banner/azione del carrello, ma qui
  // completa automaticamente il kit aggiungendo anche il prodotto in vista.
  const pageBundleSuggestion = useMemo(
    () => getBundleSuggestion(matchingBundles, productsById, cart.items),
    [matchingBundles, productsById, cart.items]
  );

  useEffect(() => {
    setData(null); setNotFound(false); setActiveImg(0);
    window.scrollTo(0, 0);
    (async () => {
      try {
        const { data: d } = await axios.get(`${API}/product/${slug}`);
        setData(d);
        setPickedFlavor(d.product.flavors?.[0] || "");
        setPickedSize(d.product.sizes?.[0] || "");
        const { data: r } = await axios.get(`${API}/reviews/${d.product.id}`);
        setReviews(r);
        trackViewItem(d.product);
      } catch {
        setNotFound(true);
      }
    })();
  }, [slug]);

  // SEO: title, OG meta + JSON-LD Product schema
  useEffect(() => {
    if (!product) return;
    const title = `${product.name} · ${product.brand} | LEOVERA Nutrition`;
    document.title = title;
    setMeta("description", (product.desc || "").slice(0, 160));
    setMeta("og:title", title, true);
    setMeta("og:description", (product.desc || "").slice(0, 200), true);
    setMeta("og:image", product.image || "", true);
    setMeta("og:type", "product", true);
    setMeta("og:url", window.location.href, true);
    setMeta("twitter:card", "summary_large_image");

    const ld = {
      "@context": "https://schema.org",
      "@type": "Product",
      name: product.name,
      brand: { "@type": "Brand", name: product.brand },
      description: product.desc,
      image: product.images?.length ? product.images : [product.image].filter(Boolean),
      offers: {
        "@type": "Offer",
        availability: Number(product.stock || 0) > 0
          ? "https://schema.org/InStock"
          : "https://schema.org/OutOfStock",
        priceCurrency: "EUR",
        url: window.location.href,
      },
    };
    if (reviews.count > 0) {
      ld.aggregateRating = { "@type": "AggregateRating", ratingValue: reviews.avg, reviewCount: reviews.count };
    }
    let script = document.getElementById("product-jsonld");
    if (!script) {
      script = document.createElement("script");
      script.type = "application/ld+json";
      script.id = "product-jsonld";
      document.head.appendChild(script);
    }
    script.textContent = JSON.stringify(ld);
    return () => {
      document.getElementById("product-jsonld")?.remove();
      document.title = "LEOVERA Nutrition";
    };
  }, [product, reviews]);

  const images = useMemo(() => {
    if (!product) return [];
    const list = (product.images || []).filter(Boolean);
    return list.length ? list : [product.image].filter(Boolean);
  }, [product]);

  if (notFound) return <NotFound />;
  if (!product) {
    return (
      <div className="min-h-screen bg-[var(--lv-bg,#060A14)] flex items-center justify-center">
        <Loader2 className="w-6 h-6 text-[#C0C0C0] animate-spin" />
      </div>
    );
  }

  const variantStock = product?.variant_stock || {};
  const selectedComboStock = stockForVariant(variantStock, pickedFlavor, pickedSize);
  const isOut = selectedComboStock !== undefined ? selectedComboStock <= 0 : Number(product.stock || 0) <= 0;
  const displayPrice = priceForSize(product, pickedSize);
  const compareAt = compareAtForSize(product, pickedSize);
  const recSize = settings?.layout?.show_recommended !== false ? recommendedSize(product) : "";
  const priceGroups = settings?.layout?.show_price_comparison !== false && !settings?.layout?.hide_prices ? formatGroups(product) : [];

  const addToCart = () => {
    cart.addItem(product, 1, { flavor: pickedFlavor, size: pickedSize });
  };

  const activatePageSuggestion = () => {
    if (!pageBundleSuggestion) return;
    applyBundleUpgrade(cart, pageBundleSuggestion.bundle, productsById);
    cart.setOpen(true);
  };

  const askRenato = () => {
    chat.openWith(`Mi interessa *${product.name}* (${product.brand}). Puoi darmi maggiori informazioni e il prezzo?`);
  };

  const submitReview = async (e) => {
    e.preventDefault();
    setSending(true); setSentMsg("");
    try {
      await userApi.post("/reviews", { product_id: product.id, rating: myRating, text: myText });
      const { data: r } = await axios.get(`${API}/reviews/${product.id}`);
      setReviews(r);
      setMyText("");
      setSentMsg("Recensione pubblicata, grazie!");
    } catch {
      setSentMsg("Errore nell'invio della recensione.");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="min-h-screen text-white" style={bgWithImageStyle()} data-testid="product-page">
      <Header />
      <main className="max-w-6xl mx-auto px-4 sm:px-6 pt-24 pb-20">
        <Link to="/#catalogo" data-testid="product-page-back"
              className="inline-flex items-center gap-2 text-[10px] tracking-[0.28em] uppercase text-[#94A3B8] hover:text-white mb-8">
          <ArrowLeft className="w-3.5 h-3.5" /> Torna al catalogo
        </Link>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
          {/* Gallery */}
          <div>
            <div className="aspect-square bg-gradient-to-b from-[#F4F5F7] to-[#D9DDE3] rounded-md overflow-hidden flex items-center justify-center">
              {images[activeImg] ? (
                <img src={images[activeImg]} alt={product.name} data-testid="product-page-image"
                     className="max-w-full max-h-full object-contain p-8" />
              ) : (
                <Package className="w-16 h-16 text-[#94A3B8]" />
              )}
            </div>
            {images.length > 1 && (
              <div className="mt-3 flex gap-2 flex-wrap">
                {images.map((img, i) => (
                  <button key={i} onClick={() => setActiveImg(i)}
                          className={`w-16 h-16 rounded-sm overflow-hidden bg-white border ${i === activeImg ? "border-[#C0C0C0]" : "border-white/10"}`}>
                    <img src={img} alt="" className="w-full h-full object-contain p-1" />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Info */}
          <div>
            <div className="flex items-start justify-between gap-3">
              <span className="text-[10px] tracking-[0.32em] uppercase text-[#C0C0C0]">
                {product.brand} · Linea {product.tier}
              </span>
              <button
                type="button"
                onClick={async () => {
                  const res = await toggleWishlist(product.id);
                  if (res.needsLogin) navigate("/login", { state: { from: `/prodotto/${slug}` } });
                }}
                data-testid="product-page-wishlist-btn"
                title={isWishlisted(product.id) ? "Rimuovi dai preferiti" : "Salva nei preferiti"}
                className={`shrink-0 inline-flex items-center justify-center w-9 h-9 rounded-full border transition-colors ${
                  isWishlisted(product.id)
                    ? "bg-red-500/15 border-red-400/50 text-red-400"
                    : "border-white/15 text-[#94A3B8] hover:text-white hover:border-white/30"
                }`}
              >
                <Heart className="w-4 h-4" fill={isWishlisted(product.id) ? "currentColor" : "none"} />
              </button>
            </div>
            <h1 className="mt-3 font-[Outfit] text-4xl sm:text-5xl font-light tracking-tight" data-testid="product-page-name">
              {product.name}
            </h1>
            {reviews.count > 0 && (
              <div className="mt-3 flex items-center gap-2">
                <Stars value={reviews.avg} testid="product-page-rating" />
                <span className="text-[11px] text-[#94A3B8]">{reviews.avg} · {reviews.count} recensioni</span>
              </div>
            )}
            <p className="mt-5 text-[#94A3B8] text-sm leading-relaxed font-[Manrope]">{product.desc}</p>

            {settings?.layout?.hide_prices ? (
              <div className="mt-6 inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#EF4444]/10 border border-[#EF4444]/40 text-[11px] font-semibold tracking-[0.24em] uppercase text-[#EF4444]"
                   data-testid="product-page-price">
                Prodotto terminato
              </div>
            ) : isPlaceholderPrice(product.price) || product.custom_price_label ? (
              <div className="mt-6 inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[var(--lv-surface,#0A1121)] border border-[#C0C0C0]/30 text-[11px] tracking-[0.24em] uppercase text-[#E5E4E2]"
                   data-testid="product-page-price">
                <Sparkles className="w-3.5 h-3.5 text-[#C0C0C0]" /> {priceLabel(product, settings)}
              </div>
            ) : (
              <div className="mt-6 inline-flex items-center gap-3 px-4 py-2 rounded-full bg-[var(--lv-surface,#0A1121)] border border-[#C0C0C0]/40 font-[Outfit]"
                   data-testid="product-page-price">
                {compareAt > displayPrice && (
                  <span className="text-[#94A3B8] text-base line-through" data-testid="product-page-compare-at">€{Number(compareAt).toFixed(2)}</span>
                )}
                <span className="text-2xl text-white">€{Number(displayPrice).toFixed(2)}</span>
              </div>
            )}

            {/* Variants */}
            {(product.flavors?.length > 0 || product.sizes?.length > 0) && (
              <div className="mt-6 space-y-4">
                {product.flavors?.length > 0 && (
                  <div>
                    <p className="text-[10px] tracking-[0.24em] uppercase text-[#94A3B8] mb-2">Gusto</p>
                    <div className="flex flex-wrap gap-1.5">
                      {product.flavors.map((f) => {
                        const stock = stockForVariant(variantStock, f, pickedSize);
                        const esaurito = stock !== undefined && stock <= 0;
                        return (
                        <button key={f} onClick={() => !esaurito && setPickedFlavor(f)} disabled={esaurito}
                                className={`px-3 py-1.5 rounded-full text-[10px] tracking-[0.14em] uppercase border ${pickedFlavor === f ? "bg-[#C0C0C0] text-[#060A14] border-[#C0C0C0]" : "border-white/15 text-[#94A3B8] hover:text-white"} ${esaurito ? "opacity-40 line-through cursor-not-allowed" : ""}`}>
                          {f}{esaurito ? " (Esaurito)" : ""}
                        </button>
                        );
                      })}
                    </div>
                  </div>
                )}
                {product.sizes?.length > 0 && (
                  <div>
                    <p className="text-[10px] tracking-[0.24em] uppercase text-[#94A3B8] mb-2">Formato</p>
                    <div className="flex flex-wrap gap-1.5">
                      {product.sizes.map((s) => {
                        const stock = stockForVariant(variantStock, pickedFlavor, s);
                        const esaurito = stock !== undefined && stock <= 0;
                        const isRec = s === recSize;
                        return (
                        <button key={s} onClick={() => !esaurito && setPickedSize(s)} disabled={esaurito}
                                data-testid={`product-page-size-${s.replace(/[^a-zA-Z0-9]+/g, "-")}`}
                                className={`px-3 py-1.5 rounded-full text-[10px] tracking-[0.14em] uppercase border ${pickedSize === s ? "bg-[#C0C0C0] text-[#060A14] border-[#C0C0C0]" : isRec ? "border-emerald-400/60 text-emerald-200" : "border-white/15 text-[#94A3B8] hover:text-white"} ${esaurito ? "opacity-40 line-through cursor-not-allowed" : ""}`}>
                          {s}{esaurito ? " (Esaurito)" : ""}{isRec ? " ★" : ""}
                        </button>
                        );
                      })}
                    </div>
                    {recSize && (
                      <p className="mt-2 text-[10px] text-emerald-300/90 tracking-[0.14em] uppercase" data-testid="product-page-recommended-note">
                        ★ {recommendedLabel(product, settings)}: {recSize} — miglior prezzo
                      </p>
                    )}
                  </div>
                )}
              </div>
            )}

            {priceGroups.length > 0 && (
              <div className="mt-7 border-t border-white/10 pt-5" data-testid="format-comparison">
                <p className="text-[10px] tracking-[0.28em] uppercase text-[#C0C0C0] mb-3">Confronto formati</p>
                <div className="space-y-4">
                  {priceGroups.map((g) => (
                    <div key={g.group}>
                      <table className="w-full text-[12px]">
                        <thead className="text-[9px] tracking-[0.2em] uppercase text-[#94A3B8]">
                          <tr>
                            <th className="text-left font-normal pb-1.5">Formato</th>
                            <th className="text-right font-normal pb-1.5">Prezzo</th>
                            <th className="text-right font-normal pb-1.5">€/{g.per100Label}</th>
                          </tr>
                        </thead>
                        <tbody>
                          {g.rows.map((r) => (
                            <tr key={r.size} className={`border-t border-white/5 ${r.isBest ? "text-emerald-200" : "text-[#E5E4E2]"}`} data-testid={`compare-row-${r.size.replace(/[^a-zA-Z0-9]+/g, "-")}`}>
                              <td className="py-1.5 uppercase tracking-wider">{r.size}{r.isBest ? " ★" : ""}</td>
                              <td className="py-1.5 text-right">€{Number(r.price).toFixed(2)}</td>
                              <td className="py-1.5 text-right font-[Outfit]">€{r.per100.toFixed(2)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="mt-8 flex flex-col sm:flex-row gap-3">
              <button onClick={addToCart} disabled={isOut} data-testid="product-page-add-to-cart"
                      className="flex-1 inline-flex items-center justify-center gap-2 bg-[#C0C0C0] text-[#060A14] px-6 py-4 rounded-sm text-[11px] tracking-[0.28em] uppercase font-medium hover:bg-white disabled:opacity-40">
                <ShoppingBag className="w-4 h-4" />
                {isOut ? "Esaurito" : "Aggiungi al carrello"}
              </button>
              <button onClick={askRenato} data-testid="product-page-ask-renato"
                      className="flex-1 inline-flex items-center justify-center gap-2 border border-emerald-500/40 text-emerald-200 px-6 py-4 rounded-sm text-[11px] tracking-[0.28em] uppercase hover:bg-emerald-500/10">
                <MessageCircle className="w-4 h-4" /> Chiedi a Leo
              </button>
            </div>

            {/* Kit suggerito: altri prodotti del kit già nel carrello */}
            {pageBundleSuggestion && (
              <div className="mt-4 p-4 rounded-sm border border-emerald-400/30 bg-gradient-to-br from-emerald-500/10 to-transparent" data-testid="product-page-bundle-suggestion">
                <div className="flex items-start gap-3">
                  <Sparkles className="w-4 h-4 text-emerald-300 mt-0.5 shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-[11px] text-emerald-200 leading-relaxed">
                      Hai già {pageBundleSuggestion.matched} prodotti del{" "}
                      <span className="text-white font-medium" data-testid="product-page-bundle-suggestion-name">{pageBundleSuggestion.bundle.name}</span>
                      {" "}nel carrello — aggiungi anche questo per completarlo.
                    </p>
                    {!settings?.layout?.hide_prices && pageBundleSuggestion.savings > 0 && (
                      <p className="mt-1 text-[10px] text-[#94A3B8]" data-testid="product-page-bundle-suggestion-savings">
                        Attiva il kit e risparmia €{pageBundleSuggestion.savings.toFixed(2)}
                      </p>
                    )}
                    <button onClick={activatePageSuggestion} data-testid="product-page-bundle-suggestion-cta"
                            className="mt-3 inline-flex items-center gap-1.5 bg-emerald-500/15 border border-emerald-400/40 text-emerald-200 px-3.5 py-2 rounded-sm text-[10px] tracking-[0.2em] uppercase font-medium hover:bg-emerald-500/25 transition-colors">
                      <Sparkles className="w-3 h-3" /> Attiva il Kit
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Details blocks */}
            <div className="mt-10 space-y-6">
              {product.benefits?.length > 0 && (
                <div>
                  <h3 className="text-[10px] tracking-[0.28em] uppercase text-[#C0C0C0] mb-3">Benefici</h3>
                  <ul className="space-y-1.5">
                    {product.benefits.map((b) => (
                      <li key={b} className="flex items-start gap-2 text-[13px] text-[#E5E4E2]">
                        <Check className="w-3.5 h-3.5 text-emerald-400 mt-0.5 shrink-0" /> {b}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {product.ingredients && (
                <div>
                  <h3 className="text-[10px] tracking-[0.28em] uppercase text-[#C0C0C0] mb-2">Ingredienti</h3>
                  <p className="text-[13px] text-[#94A3B8] leading-relaxed">{product.ingredients}</p>
                </div>
              )}
              {product.dosage && (
                <div>
                  <h3 className="text-[10px] tracking-[0.28em] uppercase text-[#C0C0C0] mb-2">Modalità d'uso</h3>
                  <p className="text-[13px] text-[#94A3B8] leading-relaxed">{product.dosage}</p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Cross-sell */}
        {data.related?.length > 0 && (
          <section className="mt-20" data-testid="product-page-crosssell">
            <h2 className="font-[Outfit] text-2xl font-light tracking-tight mb-6">
              Ti potrebbe interessare <span className="italic text-[#C0C0C0]">anche</span>
            </h2>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              {data.related.map((r) => (
                <Link key={r.id} to={`/prodotto/${r.slug}`} data-testid={`crosssell-${r.id}`}
                      className="group bg-[var(--lv-surface,#0A1121)] border border-white/10 rounded-md overflow-hidden hover:border-[#C0C0C0]/40 transition-colors">
                  <div className="aspect-square bg-white flex items-center justify-center overflow-hidden">
                    {r.image
                      ? <img src={r.image} alt={r.name} className="max-w-full max-h-full object-contain p-4 group-hover:scale-105 transition-transform duration-500" />
                      : <Package className="w-10 h-10 text-[#94A3B8]" />}
                  </div>
                  <div className="p-4">
                    <p className="text-[9px] tracking-[0.24em] uppercase text-[#94A3B8]">{r.brand}</p>
                    <p className="mt-1 font-[Outfit] text-white text-sm tracking-tight leading-snug">{r.name}</p>
                  </div>
                </Link>
              ))}
            </div>
          </section>
        )}

        {/* Complete the kit */}
        {matchingBundles.length > 0 && (
          <section className="mt-20" data-testid="product-page-bundles">
            <h2 className="font-[Outfit] text-2xl font-light tracking-tight mb-6">Completa il kit</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 max-w-4xl">
              {matchingBundles.map((b) => (
                <BundleCard key={b.id} bundle={b} productsById={productsById} compact />
              ))}
            </div>
          </section>
        )}

        {/* Reviews */}
        <section className="mt-20" data-testid="product-page-reviews">
          <h2 className="font-[Outfit] text-2xl font-light tracking-tight mb-6">
            Recensioni {reviews.count > 0 && <span className="text-[#94A3B8] text-lg">({reviews.count})</span>}
          </h2>

          {user ? (
            <form onSubmit={submitReview} data-testid="review-form"
                  className="bg-[var(--lv-surface,#0A1121)] border border-white/10 rounded-md p-5 mb-6 max-w-xl">
              <p className="text-[10px] tracking-[0.28em] uppercase text-[#94A3B8] mb-3">La tua valutazione</p>
              <Stars value={myRating} onChange={setMyRating} size="w-6 h-6" testid="review-form-stars" />
              <textarea value={myText} onChange={(e) => setMyText(e.target.value)} rows={3}
                        placeholder="Racconta la tua esperienza con questo prodotto…"
                        data-testid="review-form-text"
                        className="mt-4 w-full bg-[var(--lv-bg,#060A14)] border border-white/10 rounded-sm px-4 py-3 text-white text-sm focus:outline-none focus:border-[#C0C0C0]" />
              {sentMsg && <p className="mt-2 text-[11px] text-emerald-300" data-testid="review-form-msg">{sentMsg}</p>}
              <button type="submit" disabled={sending} data-testid="review-form-submit"
                      className="mt-3 inline-flex items-center gap-2 bg-[#C0C0C0] text-[#060A14] px-5 py-2.5 rounded-sm text-[10px] tracking-[0.24em] uppercase font-medium hover:bg-white disabled:opacity-50">
                {sending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : "Pubblica recensione"}
              </button>
            </form>
          ) : (
            <p className="text-[13px] text-[#94A3B8] mb-6">
              <Link to="/login" className="text-[#C0C0C0] underline underline-offset-2 hover:text-white">Accedi</Link>{" "}
              per lasciare una recensione.
            </p>
          )}

          {reviews.reviews.length === 0 ? (
            <p className="text-[13px] text-[#94A3B8]" data-testid="reviews-empty">Nessuna recensione ancora. Sii il primo!</p>
          ) : (
            <div className="space-y-3 max-w-2xl">
              {reviews.reviews.map((r) => (
                <div key={r.id} data-testid={`review-${r.id}`} className="bg-[var(--lv-surface,#0A1121)] border border-white/10 rounded-md p-4">
                  <div className="flex items-center justify-between">
                    <span className="text-[13px] text-white font-[Outfit]">{r.user_name}</span>
                    <Stars value={r.rating} size="w-3.5 h-3.5" />
                  </div>
                  {r.text && <p className="mt-2 text-[13px] text-[#94A3B8] leading-relaxed">{r.text}</p>}
                  <p className="mt-2 text-[10px] text-[#94A3B8]/60">{(r.created_at || "").slice(0, 10)}</p>
                </div>
              ))}
            </div>
          )}
        </section>
      </main>
      <Footer />
    </div>
  );
}
